<template>
  <ScrollArea>
    <div
      class="settings--limit-width">
      <h1 class="settings__title">Profile info</h1>

      <div
        class="settings--box big" >
        <label
          for="file"
          class="m-0">
          <div id="logo-wrapper">
            <AvatarImage
              v-if="!avatar && !processedMedia.url"
              class="avatar-icon"
              :name="firstName"
              :image="null"
              :show-availability-status="false"
              :size="SBAvatarImageSize.W68" />
            <span
              v-else
              id="logo"
              :style="logoUrl ? `background-image: url(${logoUrl}); background-size: cover; border: 1px solid ${SBColors.greyBorder};` : ''" />
            <DropHere
              :force="true"
              type="basic" />
          </div>
        </label>

        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty, pristine }" >

          <b-form-group>
            <label class="flex flex-align-center">
              First name
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="First name" >
              <b-input
                v-model="firstNameModel"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter your first name" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <b-form-group>
            <label class="flex flex-align-center">
              Last name
            </label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              name="Last name" >
              <b-input
                v-model="lastNameModel"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter your last name" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>

          <FooterActions
            :form-valid="valid || !!(processedMedia && processedMedia.url)"
            :has-changes="dirty || !!(processedMedia && processedMedia.url)"
            :show-cancel="!pristine || !!(processedMedia && processedMedia.url)"
            :save-busy="saving"
            @reset="resetChanges"
            @save="updateNotificationSettings" />
        </ValidationObserver>
      </div>

    </div>
  </ScrollArea>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ScrollArea from '@/components/common/ScrollArea';
import ToasterMixin from '@/mixins/Toaster';
import FooterActions from '@/components/business/settings/partials/FooterActions';
import NoPermissionMixin from '@/mixins/AccessPermission';
import AvatarImage from '@/components/common/AvatarImage';
import SBAvatarImageSize from '@/helpers/lists/avatarSize';
import DropHere from '@/components/common/DropHere';
import { getFileExtension, getMediaType } from '@/helpers/mediaHelpers';

export default {
  name: 'Profile',
  components: {
    DropHere,
    FooterActions,
    ScrollArea,
    AvatarImage,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
  ],
  data() {
    return {
      saving: false,
      newFirstName: null,
      newLastName: null,
      processedMedia: {},
      SBAvatarImageSize,
    };
  },
  computed: {
    ...mapGetters({
      id: 'user/id',
      avatar: 'user/avatar',
      firstName: 'user/firstName',
      lastName: 'user/lastName',
      apiError: 'automation/apiError',
    }),
    firstNameModel: {
      get() {
        let returnValue;
        if (this.newFirstName) {
          returnValue = this.newFirstName;
        }
        return returnValue;
      },
      set(changedContent) {
        this.newFirstName = changedContent;
        this.resetValidator();
      },
    },
    lastNameModel: {
      get() {
        let returnValue;
        if (this.newLastName) {
          returnValue = this.newLastName;
        }
        return returnValue;
      },
      set(changedContent) {
        this.newLastName = changedContent;
        this.resetValidator();
      },
    },
    logoUrl() {
      return !this.resettingData && this.processedMedia.url ? this.processedMedia.url : this.avatar;
    },
  },
  watch: {
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.showToaster(error.data.errors, null, null, 'error');
      } else {
        this.showToaster(error.message || 'Something went wrong. Please try again.', null, null, 'error');
      }
    },
    firstName() {
      this.firstNameModel = this.firstName;
    },
    lastName() {
      this.lastNameModel = this.lastName;
    },
  },
  mounted() {
    if (this.firstName) {
      this.firstNameModel = this.firstName;
      this.lastNameModel = this.lastName;
    }

    this.$eventBus.$on('mediaDrop', this.mediaDropHandler);

    this.finalizeSplashView();
  },
  beforeDestroy() {
    this.$eventBus.$off('mediaDrop', this.mediaDropHandler);
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      updateMemberData: 'user/updateMemberData',
      updateMemberAvatar: 'user/updateMemberAvatar',
    }),
    resetChanges() {
      this.firstNameModel = this.firstName;
      this.lastNameModel = this.lastName;
      this.processedMedia = {};
    },
    updateNotificationSettings() {
      this.saving = true;
      const formData = new FormData();
      const requests = [];

      // update avatar
      if (this.processedMedia.file) {
        formData.append('avatar', this.processedMedia.file);

        const avatarRequest = this.updateMemberAvatar(formData)
          .then(() => {
            this.processedMedia = {};
          });

        requests.push(avatarRequest);
      }

      // update data
      if (this.newFirstName !== this.firstName || this.newLastName !== this.lastName) {
        const dataPayload = {
          first_name: this.newFirstName ? this.newFirstName : this.firstName,
          last_name: this.newLastName ? this.newLastName : this.lastName,
        };

        const dataRequest = this.updateMemberData(dataPayload);

        requests.push(dataRequest);
      }

      Promise.all(requests)
        .then(() => {
          this.showToaster('Profile info has been successfully updated.', null, null, 'OK');
        })
        .catch(() => {
          this.showToaster('Something went wrong. Please try again.', null, null, 'error');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    resetValidator() {
      // Reset the validator if message edit was undone
      if (
        this.firstName === this.newFirstName &&
        this.lastName === this.newLastName &&
        this.emptyObject(this.processedMedia)
      ) {
        this.$nextTick(() => { this.$refs.customFieldObserver.reset(); });
      }
    },
    /**
     * Handler for media drop event. Accepts single file or array of files.
     *
     * @param droppedMedia Media file(s) dropped on the messages section.
     */
    mediaDropHandler(droppedMedia) {
      if (Array.isArray(droppedMedia)) {
        this.processMedia(droppedMedia[0]);
      } else {
        this.processMedia(droppedMedia);
      }
    },
    /**
     * Processes media file setting it url, thumbnail for video, caption, type etc.
     *
     * @param media Media to process.
     */
    processMedia(media) {
      let newMedia = {};
      const URLObj = window.URL || window.webkitURL;
      const mediaType = media.file.type ? getMediaType(media.file.type) : getMediaType(getFileExtension(media.name));

      if (mediaType === 'video') {
        return;
      }

      newMedia = {
        id: media.id,
        name: media.name,
        url: URLObj.createObjectURL(media.file),
        thumbnail: null,
        caption: '',
        mediaType,
        file: media.file,
      };

      this.processedMedia = newMedia;
      this.processedMedia = { ...this.processedMedia };
    },
  },
};
</script>

<style lang="scss" scoped>
.avatar-icon {
  // margin-bottom: 15px;
}

#logo-wrapper {
  width: 76px;
  height: 76px;
  margin: 0px 0px 11px;
  padding: 4px;
  border-radius: 5px;
  position: relative;

  &:hover {
    background-color: $linkHover;
    cursor: pointer;
  }

  #logo {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 500px;
    background-color: #deedfe;
    background-image: url('~@/assets/img/SB-Icons/user-business-blue.svg');
    background-size: 35px;
    background-repeat: no-repeat;
    background-position: center;
  }
}

::v-deep .text-input-component .text-input-wrapper {
    max-height: unset;

    .input-scroll-area {
      height: unset;
      min-height: 72px;
      max-height: calc(100vh - 400px);
    }
}
</style>
